import { useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ArrowRight, Mail } from 'lucide-react';

import { DoneRounded } from "@material-ui/icons";

import loginService from "../../../domain/services/login.service";
import packageService from "../../../domain/services/financial/package.service";
import ModalInviteWhatsapp from '../../../components/marketplace/checkout/modalInviteWhatsapp';
import announcementService from '../../../domain/services/marketing/announcement.service';
import { announcements } from '../../../domain/constant/announcements.constant';
import PostPurchaseEvents from "./postPurchaseEvents/postPurchaseEvents";
import yearlyEventsService from '../../../domain/services/marketing/events/yearly.events.service';
import urlQueryProvider from '../../../providers/url.query.provider';
import { PROMOTION_VALID_DAYS_PARAM } from './purchaseThank.url';

const formatValidity = (validDays, months) => {
    if (months) {
        if (months >= 12 && months % 12 === 0) return `${months / 12} ${months / 12 === 1 ? 'ano' : 'anos'} de acesso`;
        return `${months} ${months === 1 ? 'mês' : 'meses'} de acesso`;
    }
    if (validDays) {
        if (validDays >= 365) return `${Math.round(validDays / 365)} ${Math.round(validDays / 365) === 1 ? 'ano' : 'anos'} de acesso`;
        return `${validDays} dias de acesso`;
    }
    return null;
};

const PurchaseThank = ({ isRegister: isRegisterProp }) => {

    const user = loginService.getUserAuthentication();
    const history = useHistory();
    const { search } = useLocation();

    const productId = urlQueryProvider.parseIntFromQueryString(search, 'productId');
    const promotionValidDays = urlQueryProvider.parseIntFromQueryString(search, PROMOTION_VALID_DAYS_PARAM);
    const cameFromRegister = sessionStorage.getItem('@facilita-app/purchaseFromRegister') === 'true';
    if (cameFromRegister) sessionStorage.removeItem('@facilita-app/purchaseFromRegister');
    const isRegister = isRegisterProp || cameFromRegister;

    const [alertStatus, setAlertStatus] = useState({ open: false });
    const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
    const [hasActiveEvent, setHasActiveEvent] = useState(null);
    const [product, setProduct] = useState(null);

    useEffect(() => {
        const currentEvent = yearlyEventsService.getCurrentEvent();
        setHasActiveEvent(!!currentEvent);

        if (!currentEvent && !isRegister)
            openWhatsAppModal();

        if (productId)
            packageService.findPackageInfoById(productId)
                .then(res => setProduct(res.data))
                .catch(() => null);
    }, []);

    const openWhatsAppModal = () => {
        const alreadyShowedWhatsappInvite = announcementService.isShowed(announcements.postPurchaseWhatsAppInvite);

        if (alreadyShowedWhatsappInvite) return;

        setShowWhatsAppModal(true);
    }

    const handleEventFinish = () => {
        setHasActiveEvent(false);
        openWhatsAppModal();
    };

    const renderDefaultView = () => {
        const planName = product ? (product.name || '').replace(/^Pacote\s+/i, '') : null;
        const amount = product?.packageAmount || 1;
        const validity = product ? formatValidity(promotionValidDays || product.validDays, promotionValidDays ? null : product.months) : null;
        const summaryParts = [planName, amount > 1 ? `${amount} pacotes` : null, validity].filter(Boolean);
        const email = user?.email;

        return (
            <>
                <div className="bg-gray-50 min-h-[calc(100vh-64px)]">
                    <main className="flex flex-col items-center py-10 px-4">
                        <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex flex-col items-center text-center py-10 px-6">
                                {/* Icon */}
                                <div className="relative mb-5">
                                    <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                                        <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                                            <DoneRounded className="!w-14 !h-14 text-white" />
                                        </div>
                                    </div>
                                </div>

                                {/* Title */}
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Confirmado!</h1>
                                <p className="text-sm text-gray-500 mb-6">
                                    Seu saldo já está disponível. Agradecemos sua confiança.
                                </p>

                                {/* Purchase summary */}
                                {summaryParts.length > 0 && (
                                    <p className="text-sm text-gray-400 mb-8">
                                        {summaryParts.join(' · ')}
                                    </p>
                                )}

                                {/* CTAs */}
                                <div className="w-full space-y-3 mb-8">
                                    <button
                                        onClick={() => history.push('/solicitations#new')}
                                        className="w-full h-12 rounded-lg bg-main text-white font-semibold text-base flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                                    >
                                        Solicitar um serviço
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => history.push('/home')}
                                        className="w-full h-12 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
                                    >
                                        Acessar Plataforma
                                    </button>
                                </div>

                                {/* Email confirmation */}
                                {email && (
                                    <>
                                        <div className="w-full border-t border-gray-100 mb-5" />
                                        <p className="text-xs text-gray-400">
                                            <Mail className="h-3.5 w-3.5 inline mr-1.5 relative -top-px" />
                                            Enviamos um e-mail de confirmação para <span className="font-medium text-gray-500">{email}</span> com os detalhes da sua compra.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </main>
                </div>

                <Snackbar
                    onClose={() => setAlertStatus({ ...alertStatus, open: false })}
                    open={alertStatus.open}
                    autoHideDuration={2000}
                    key={alertStatus.date}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity={alertStatus.severity}>
                        {alertStatus.text}
                    </Alert>
                </Snackbar>

                <ModalInviteWhatsapp
                    open={showWhatsAppModal}
                    onClose={() => setShowWhatsAppModal(false)}
                />
            </>
        );
    };

    if (hasActiveEvent === null)
        return null;

    return (
        <>
            <Helmet>
                <title>Compra Finalizada - Facilita Jurídico</title>
                <meta name="description" content="Sua compra foi finalizada com sucesso. Cadastre sua solicitação em nossa plataforma." />
            </Helmet>

            {hasActiveEvent
                ? <PostPurchaseEvents onFinish={handleEventFinish} />
                : renderDefaultView()
            }
        </>
    );
}

export default PurchaseThank;
